import { useState } from 'react';
import { CameraAltOutlined, DeleteOutlineOutlined } from '@mui/icons-material';
import { Backdrop, Box, Button, Grid2, IconButton, Stack } from '@mui/material';
import { Webcam } from '@zenput/feature-zencam';
import { useSnackbar } from 'notistack';
import { makeStyles } from 'tss-react/mui';
import { ZenputFiles } from '../../../backbone/shared/models/files';
import {
    collectCameraNative,
    collectMediaNative,
    collectMediaWeb,
    getZenputFiles
} from '../../../backbone-unorganized/followUpActions/models';
import { createUUID } from '../../../backbone-unorganized/utils/files';
import { MIME_TYPE_TO_EXTENSION } from '../../../constants';
import { isWeb } from '../../../shared/utils/is';
import { ImageVideoPickerDialog } from '../../shared/ImageVideoPickerDialog';
import { SimpleGallery } from '../../shared/SimpleGallery/SimpleGallery';
import { FollowUpActionFileType, WebMediaResponseType, ZenputFileType } from './FollowUpActions.types';

const useStyles = makeStyles()((theme) => {
    const { spacing } = theme;

    return {
        imgBtn: {
            border: 'dashed',
            borderWidth: 'thin',
            color: 'gray',
            padding: spacing(1),
            textTransform: 'none',
            width: '-webkit-fill-available'
        },
        imgBtnReply: {
            border: '.5px solid lightgray',
            color: 'gray',
            marginBottom: spacing(2),
            padding: spacing(1),
            textTransform: 'none',
            width: '-webkit-fill-available'
        },
        boxImageContainer: {
            height: spacing(14)
        },
        imgPreview: {
            height: '100%',
            objectFit: 'cover',
            width: '100%'
        }
    };
});

interface FollowUpActionsGalleryProps {
    handleAddS3Keys: (value) => void;
    handleDeleteS3keys: (index) => void;
    isReply: boolean;
    onChange: (images: FollowUpActionFileType[]) => void;
    uploadFollowUpFiles: (files: ZenputFileType[]) => string[];
    value: FollowUpActionFileType[];
}

export const FollowUpActionsGallery = (props: FollowUpActionsGalleryProps) => {
    const { handleAddS3Keys, handleDeleteS3keys, isReply, onChange, uploadFollowUpFiles, value } = props;
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [galleryOpen, toggleGallery] = useState(false);
    const [openCamera, toggleOpenCamera] = useState(false);
    const [showImageDialog, setShowImageDialog] = useState(false);

    const removeImage = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
        handleDeleteS3keys(index);
    };

    const handleOnSelect = async (option: string) => {
        setShowImageDialog(false);
        if (!option) return;

        if (option === 'browse') {
            if (isWeb()) {
                const media: WebMediaResponseType = await collectMediaWeb();

                let keys;
                try {
                    keys = await uploadFollowUpFiles(media.files);
                    handleAddS3Keys(keys);
                    onChange([...value, ...media.displayUrls]);
                } catch (error) {
                    enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                        variant: 'error'
                    });
                    keys = [];
                }
            } else {
                const media = await collectMediaNative();
                const files = getZenputFiles(media.map((f) => f.path));

                let keys;
                try {
                    keys = await uploadFollowUpFiles(files);
                    handleAddS3Keys(keys);
                    const displayUrls = media.map((f) => ({
                        name: f.path ? f.path.split('/').pop() : `temp.${f.format}`,
                        raw_file: f.webPath,
                        type: f.format
                    }));
                    onChange([...value, ...displayUrls]);
                } catch (error) {
                    enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                        variant: 'error'
                    });
                    keys = [];
                }
            }
        }

        if (option === 'camera') {
            if (isWeb()) {
                toggleOpenCamera(true);
            } else {
                const photo = await collectCameraNative();
                let keys;
                try {
                    keys = await uploadFollowUpFiles(getZenputFiles([photo.path]));
                    handleAddS3Keys(keys);
                    const displayUrls = {
                        name: photo.path ? photo.path.split('/').pop() : `temp.${photo.format}`,
                        raw_file: photo.webPath,
                        type: photo.format
                    };
                    onChange([...value, displayUrls]);
                } catch (error) {
                    enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                        variant: 'error'
                    });
                    keys = [];
                }
            }
        }
    };

    return (
        <Stack>
            {!isReply && (
                <Button
                    className={classes.imgBtn}
                    component="label"
                    onClick={() => setShowImageDialog(true)}
                    startIcon={<CameraAltOutlined />}
                >
                    {_t('mobile_field_image_button_add')}
                </Button>
            )}

            {galleryOpen && (
                <SimpleGallery
                    items={value.map((src, index) => ({ id: index + src.raw_file, src: src.raw_file }))}
                    onClose={() => toggleGallery(false)}
                    startIndex={0}
                />
            )}
            {value.length > 0 && (
                <Grid2
                    container
                    direction="row"
                    spacing={2}
                    sx={{ borderTop: isReply && '.5px solid lightgray', marginTop: 2, marginBottom: 2 }}
                >
                    {value.map((src, index) => (
                        <Grid2 key={`s3-${index}`} size={{ xs: 4, md: 3 }}>
                            <Box
                                className={classes.boxImageContainer}
                                sx={{ marginTop: isReply && 2, position: 'relative' }}
                            >
                                <img
                                    alt={`preview ${index}`}
                                    className={classes.imgPreview}
                                    onClick={() => toggleGallery(true)}
                                    src={src.raw_file}
                                />
                                <IconButton
                                    onClick={() => removeImage(index)}
                                    size="small"
                                    sx={{
                                        bgcolor: 'red',
                                        color: 'white',
                                        position: 'absolute',
                                        right: 4,
                                        top: 4
                                    }}
                                >
                                    <DeleteOutlineOutlined fontSize="small" />
                                </IconButton>
                            </Box>
                        </Grid2>
                    ))}
                </Grid2>
            )}

            {isReply && (
                <Button
                    className={classes.imgBtnReply}
                    component="label"
                    onClick={() => setShowImageDialog(true)}
                    startIcon={<CameraAltOutlined />}
                >
                    {_t('mobile_field_image_button_add')}
                </Button>
            )}

            {showImageDialog && (
                <ImageVideoPickerDialog
                    onSelect={handleOnSelect}
                    title={_t('mobile_field_image_picker_dialog_title')}
                />
            )}

            {openCamera && (
                <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <Webcam
                        mode="photo"
                        onCapture={async (blob: Blob) => {
                            const extension = blob.type.split('/')[1] || 'jpg';
                            const fileName = `capture_${Date.now()}.${extension}`;

                            const file = new File([blob], fileName, { type: blob.type });

                            const previewUrl = URL.createObjectURL(file);
                            const fileToUpload = {
                                name: file.name,
                                raw_file: previewUrl,
                                size: file.size,
                                type: file.type
                            };

                            const uniqueFileId = createUUID();
                            const extensionFile = MIME_TYPE_TO_EXTENSION[file.type];
                            const zenputFiles = new ZenputFiles([
                                {
                                    id: uniqueFileId,
                                    mimetype: extensionFile,
                                    name: file.name,
                                    raw_file: file,
                                    size: file.size,
                                    url_base64: previewUrl
                                }
                            ]);

                            let keys;
                            try {
                                keys = await uploadFollowUpFiles(zenputFiles);
                                handleAddS3Keys(keys);
                                onChange([...value, ...[fileToUpload]]);
                            } catch (error) {
                                enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                                    variant: 'error'
                                });
                                keys = [];
                            }

                            toggleOpenCamera(false);
                        }}
                        onGoBack={() => {
                            toggleOpenCamera(false);
                        }}
                    />
                </Backdrop>
            )}
        </Stack>
    );
};
